import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, Truck, Calendar } from 'lucide-react';
import '../styles/MasterForms.css';
import apiClient from '../services/api';

const Vehicles = () => {
  // ✅ Get session data from localStorage
  const getSessionData = () => {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      
      const sessionData = JSON.parse(sessionStr);
      return sessionData?.session || sessionData;
    } catch (error) {
      console.error('Error parsing session data:', error);
      return null;
    }
  };

  const session = getSessionData();
  const companyId = session?.companyid;
  const yearId = session?.yearid;

  // ✅ ALL HOOKS FIRST (before any conditional returns)
  const [vehicles, setVehicles] = useState([]);
  const [filteredVehicles, setFilteredVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  
  const [formData, setFormData] = useState({
    vehicleid: null,
    companyid: companyId || null,  // ✅ From session
    registrationno: '',
    vehicletype: '',
    model: '',
    year: '',
    isactive: true
  });
  
  const [errors, setErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);

  // Common vehicle types
  const vehicleTypes = [
    'Truck',
    'Trailer',
    'Container Truck',
    'Flatbed Truck',
    'Refrigerated Truck',
    'Tanker',
    'Van',
    'Pickup Truck',
    'Heavy Truck',
    'Light Truck',
    'Other'
  ];

  // Generate year options (current year to 30 years back)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 31 }, (_, i) => currentYear - i);

  useEffect(() => {
    if (!companyId || !yearId) {
      setLoading(false);
      return;
    }
    fetchVehicles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, yearId]);

  useEffect(() => {
    filterVehicles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, filterType, vehicles]);

  const fetchVehicles = async () => {
    setLoading(true);
    try {
      // ✅ Use apiClient with session companyId
      const response = await apiClient.get('/Vehicles.php', {
        params: { companyid: companyId }
      });
      
      if (response.data.success) {
        setVehicles(response.data.data || []);
      } else {
        alert('Failed to fetch vehicles: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else {
        alert('Error fetching vehicles. Please check if backend is running.');
      }
    } finally {
      setLoading(false);
    }
  };

  const filterVehicles = () => {
    let filtered = vehicles;

    // Filter by type
    if (filterType !== 'all') {
      filtered = filtered.filter(vehicle => vehicle.VehicleType === filterType);
    }

    // Filter by search term
    if (searchTerm.trim()) {
      filtered = filtered.filter(vehicle =>
        vehicle.RegistrationNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vehicle.Model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vehicle.VehicleType?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredVehicles(filtered);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.registrationno.trim()) {
      newErrors.registrationno = 'Registration number is required';
    } else if (formData.registrationno.length < 3) {
      newErrors.registrationno = 'Registration number must be at least 3 characters';
    }
    
    if (formData.year && (isNaN(formData.year) || formData.year < 1900 || formData.year > currentYear + 1)) {
      newErrors.year = `Year must be between 1900 and ${currentYear + 1}`;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitLoading(true);
    
    try {
      // ✅ Use apiClient
      const method = editMode ? 'put' : 'post';
      const response = await apiClient[method]('/Vehicles.php', {
        ...formData,
        companyid: companyId,  // ✅ Use session companyId
        isactive: formData.isactive ? 1 : 0
      });
      
      const data = response.data;
      
      if (data.success) {
        alert(editMode ? 'Vehicle updated successfully!' : 'Vehicle created successfully!');
        setShowModal(false);
        resetForm();
        fetchVehicles();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error saving vehicle:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else {
        alert('Error saving vehicle. Please try again.');
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = (vehicle) => {
    setFormData({
      vehicleid: vehicle.VehicleID,
      companyid: vehicle.CompanyID,
      registrationno: vehicle.RegistrationNo,
      vehicletype: vehicle.VehicleType || '',
      model: vehicle.Model || '',
      year: vehicle.Year || '',
      isactive: vehicle.IsActive === 1
    });
    setEditMode(true);
    setShowModal(true);
    setErrors({});
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this vehicle?')) {
      return;
    }
    
    try {
      // ✅ Use apiClient
      const response = await apiClient.delete('/Vehicles.php', {
        data: { vehicleid: id }
      });
      
      const data = response.data;
      
      if (data.success) {
        alert('Vehicle deleted successfully!');
        fetchVehicles();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else {
        alert('Error deleting vehicle. Please try again.');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      vehicleid: null,
      companyid: companyId || null,  // ✅ From session
      registrationno: '',
      vehicletype: '',
      model: '',
      year: '',
      isactive: true
    });
    setEditMode(false);
    setErrors({});
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  // Get unique vehicle types from existing vehicles for filter
  const uniqueTypes = [...new Set(vehicles.map(v => v.VehicleType).filter(Boolean))];

  // ✅ Check session context - AFTER all hooks
  if (!companyId || !yearId) {
    return (
      <div className="master-container">
        <div className="empty-state">
          <h3>No Session Context</h3>
          <p>Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="master-container">
      <div className="master-header">
        <div className="header-left">
          <h2>Vehicles Master</h2>
          <p className="subtitle">Manage fleet vehicles and registration</p>
        </div>
        <button className="btn-primary" onClick={openAddModal}>
          <Plus size={20} />
          Add Vehicle
        </button>
      </div>

      <div className="master-toolbar">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by registration, model, or type..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="btn-clear" onClick={() => setSearchTerm('')}>
              <X size={16} />
            </button>
          )}
        </div>
        
        {uniqueTypes.length > 0 && (
          <div className="filter-group">
            <select 
              value={filterType} 
              onChange={(e) => setFilterType(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Types</option>
              {uniqueTypes.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
        )}

        <div className="toolbar-info">
          Total: {filteredVehicles.length} {filteredVehicles.length === 1 ? 'vehicle' : 'vehicles'}
        </div>
      </div>

      {loading ? (
        <div className="loading-state">Loading vehicles...</div>
      ) : filteredVehicles.length === 0 ? (
        <div className="empty-state">
          <p>No vehicles found</p>
          {(searchTerm || filterType !== 'all') && (
            <button onClick={() => { setSearchTerm(''); setFilterType('all'); }}>
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Registration No</th>
                <th>Vehicle Type</th>
                <th>Model</th>
                <th>Year</th>
                <th>Status</th>
                <th>Added On</th>
                <th className="actions-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredVehicles.map((vehicle) => (
                <tr key={vehicle.VehicleID}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Truck size={16} color="#667eea" />
                      <span className="font-mono" style={{ fontWeight: '700', fontSize: '14px' }}>
                        {vehicle.RegistrationNo}
                      </span>
                    </div>
                  </td>
                  <td>
                    {vehicle.VehicleType ? (
                      <span className="type-badge" style={{
                        background: '#dbeafe',
                        color: '#1e40af',
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {vehicle.VehicleType}
                      </span>
                    ) : '-'}
                  </td>
                  <td className="font-semibold">{vehicle.Model || '-'}</td>
                  <td>
                    {vehicle.Year ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Calendar size={14} color="#6b7280" />
                        <span>{vehicle.Year}</span>
                      </div>
                    ) : '-'}
                  </td>
                  <td>
                    <span className={`status-badge ${vehicle.IsActive ? 'active' : 'inactive'}`}>
                      {vehicle.IsActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{new Date(vehicle.CreatedAt).toLocaleDateString()}</td>
                  <td className="actions-column">
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(vehicle)}
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(vehicle.VehicleID)}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMode ? 'Edit Vehicle' : 'Add New Vehicle'}</h3>
              <button className="btn-close" onClick={closeModal}>
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-section">
                <h4>Vehicle Information</h4>
                
                <div className="form-group">
                  <label htmlFor="registrationno">
                    Registration Number <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="registrationno"
                    value={formData.registrationno}
                    onChange={(e) => setFormData({ ...formData, registrationno: e.target.value.toUpperCase() })}
                    maxLength="50"
                    placeholder="e.g., ABC-1234, KSA-5678"
                    className={errors.registrationno ? 'error' : ''}
                    style={{ fontFamily: 'monospace', fontSize: '16px', fontWeight: '600' }}
                  />
                  {errors.registrationno && (
                    <span className="error-message">{errors.registrationno}</span>
                  )}
                  <small style={{ color: '#6b7280', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                    Enter license plate or registration number
                  </small>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="vehicletype">Vehicle Type</label>
                    <select
                      id="vehicletype"
                      value={formData.vehicletype}
                      onChange={(e) => setFormData({ ...formData, vehicletype: e.target.value })}
                    >
                      <option value="">-- Select Type --</option>
                      {vehicleTypes.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="year">Year</label>
                    <select
                      id="year"
                      value={formData.year}
                      onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                      className={errors.year ? 'error' : ''}
                    >
                      <option value="">-- Select Year --</option>
                      {years.map((year) => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                    {errors.year && (
                      <span className="error-message">{errors.year}</span>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="model">Model</label>
                  <input
                    type="text"
                    id="model"
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                    maxLength="100"
                    placeholder="e.g., Mercedes-Benz Actros, Volvo FH16"
                  />
                  <small style={{ color: '#6b7280', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                    Make and model of the vehicle
                  </small>
                </div>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.isactive}
                    onChange={(e) => setFormData({ ...formData, isactive: e.target.checked })}
                  />
                  <span>Active</span>
                </label>
                <small style={{ color: '#6b7280', fontSize: '12px', display: 'block', marginTop: '4px', marginLeft: '28px' }}>
                  Inactive vehicles won't appear in assignment lists
                </small>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeModal}
                  disabled={submitLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitLoading}
                >
                  {submitLoading ? 'Saving...' : (editMode ? 'Update Vehicle' : 'Add Vehicle')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Vehicles;